<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import type { PropType } from 'vue';
import {
    ref,
    unref,
    watch,
} from 'vue';

import { useGenerateIds } from '#public_ts/composables';

const props = defineProps({
    id: { type: String as PropType<string | undefined>, required: false, default: undefined },
    name: { type: String as PropType<string | undefined>, required: false, default: undefined },
    modelValue: { type: [String, Number, null] as PropType<string | number | null | undefined>, required: false, default: undefined },
    type: { type: String as PropType<string | undefined>, required: false, default: undefined },
    delay: { type: Number as PropType<number>, required: false, default: 200 },
    maxWait: { type: Number as PropType<number>, required: false, default: 500 },
    applyImmediately: { type: Number as PropType<number>, required: false, default: 0 },
});
const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
    (event: 'enter', value: string): void;
}>();

const generateIds = useGenerateIds();
const controlId = props.id || generateIds();
const controlName = (props.name === '--') ? undefined : (props.name || controlId);
const localValue = ref(String(props.modelValue ?? ''));

function _applyImmediately() {
    emit('update:modelValue', unref(localValue));
}

function onEnter() {
    _applyImmediately();
    emit('enter', unref(localValue));
}

const debouncedUpdate = useDebounceFn(() => {
    _applyImmediately();
}, () => props.delay, { maxWait: () => props.maxWait });

watch(() => props.modelValue, (newValue) => {
    localValue.value = String(newValue ?? '');
});

watch(() => props.applyImmediately, () => {
    _applyImmediately();
});

function onInput(e: Event) {
    localValue.value = (e.target as HTMLInputElement)?.value;
    debouncedUpdate();
}

</script>
<template>
    <input
        :id="controlId"
        :name="controlName"
        :type="props.type || 'text'"
        :value="localValue"
        @input="onInput"
        @keyup="onInput"
        @blur="_applyImmediately"
        @keydown.enter="onEnter"
    >
</template>
